/**
 * Servicio para gestión de roles
 * Sistema Klever Octopus - Betel Natural
 */
import apiClient, { handleApiError, formatApiResponse } from './api';

// Endpoints para roles
const ENDPOINTS = {
  ALL_ROLES: '/roles', // Endpoint RBAC (cambiado de /users/roles a /roles)
  ROLE_BY_ID: (id) => `/roles/${id}`,
  ROLE_PERMISSIONS: (id) => `/roles/${id}/permissions`,
  ROLE_USERS: (id) => `/roles/${id}/users`
};

/**
 * Obtener lista de roles (la API devuelve todos los roles)
 */
export const getRoles = async (params = {}) => {
  try {
    const response = await apiClient.get(ENDPOINTS.ALL_ROLES);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener todos los roles (sin paginación)
 */
export const getAllRoles = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.ALL_ROLES);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener rol por ID
 */
export const getRoleById = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.ROLE_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Alias para getRoleById (compatibilidad)
 */
export const getRole = getRoleById;

/**
 * Crear nuevo rol
 */
export const createRole = async (roleData) => {
  try {
    // Mapear nivel_acceso a level si viene del formulario
    const level = roleData.nivel_acceso
      ? ['basic', 'intermediate', 'advanced', 'admin'][roleData.nivel_acceso - 1]
      : (roleData.level || 'basic');

    const payload = {
      name: roleData.name,
      display_name: roleData.display_name,
      description: roleData.description || null,
      color: roleData.color || '#6c757d',
      level: level,
      is_active: roleData.is_active !== undefined ? roleData.is_active : true,
      permissions: roleData.permissions || [],
      // Configuraciones específicas del rol
      settings: {
        dashboard_access: roleData.dashboard_access || true,
        can_export_data: roleData.can_export_data || false,
        can_bulk_actions: roleData.can_bulk_actions || false,
        max_records_per_page: roleData.max_records_per_page || 50,
        session_timeout: roleData.session_timeout || 480, // minutos
        ...roleData.settings
      }
    };

    console.log('Creating role with payload:', payload);

    const response = await apiClient.post(ENDPOINTS.ALL_ROLES, payload);

    return formatApiResponse(response);
  } catch (error) {
    console.error('Error creating role:', error);
    return handleApiError(error);
  }
};

/**
 * Actualizar rol
 */
export const updateRole = async (id, roleData) => {
  try {
    const response = await apiClient.put(ENDPOINTS.ROLE_BY_ID(id), {
      name: roleData.name,
      display_name: roleData.display_name,
      description: roleData.description,
      color: roleData.color,
      level: roleData.level,
      is_active: roleData.is_active,
      permissions: roleData.permissions || [],
      settings: {
        dashboard_access: roleData.dashboard_access,
        can_export_data: roleData.can_export_data,
        can_bulk_actions: roleData.can_bulk_actions,
        max_records_per_page: roleData.max_records_per_page,
        session_timeout: roleData.session_timeout,
        ...roleData.settings
      }
    });
    
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Eliminar rol (soft delete)
 */
export const deleteRole = async (id) => {
  try {
    const response = await apiClient.delete(ENDPOINTS.ROLE_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener permisos de un rol
 */
export const getRolePermissions = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.ROLE_PERMISSIONS(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Asignar permisos a un rol
 */
export const assignPermissions = async (roleId, permissionIds) => {
  try {
    const response = await apiClient.put(ENDPOINTS.ROLE_PERMISSIONS(roleId), {
      permissions: permissionIds
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener usuarios asignados a un rol
 */
export const getRoleUsers = async (id, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 20,
      search: params.search || '',
      status: params.status || ''
    });
    
    const response = await apiClient.get(`${ENDPOINTS.ROLE_USERS(id)}?${queryParams}`);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Duplicar rol (crear copia)
 */
export const duplicateRole = async (id, newRoleData) => {
  try {
    const response = await apiClient.post(`${ENDPOINTS.ROLE_BY_ID(id)}/duplicar`, {
      name: newRoleData.name,
      display_name: newRoleData.display_name,
      description: newRoleData.description,
      color: newRoleData.color,
      level: newRoleData.level
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Validar nombre de rol único
 */
export const validateUniqueRoleName = async (name, excludeRoleId = null) => {
  try {
    const response = await apiClient.post('/roles/validar-nombre', {
      name: name,
      exclude_role_id: excludeRoleId
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener estadísticas de roles
 */
export const getRoleStats = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.ROLE_STATS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Exportar configuración de roles
 */
export const exportRoles = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      format: params.format || 'json', // json, csv, excel
      include_permissions: params.include_permissions || true,
      include_users: params.include_users || false,
      role_ids: params.role_ids ? params.role_ids.join(',') : ''
    });
    
    const response = await apiClient.get(`/roles/exportar?${queryParams}`, {
      responseType: 'blob'
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Importar configuración de roles
 */
export const importRoles = async (file, options = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('merge_strategy', options.merge_strategy || 'skip'); // skip, overwrite, merge
    formData.append('create_missing_permissions', options.create_missing_permissions || false);
    
    const response = await apiClient.post('/roles/importar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener jerarquía de roles
 */
export const getRoleHierarchy = async () => {
  try {
    const response = await apiClient.get('/roles/jerarquia');
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener plantillas de roles predefinidas
 */
export const getRoleTemplates = async () => {
  try {
    const response = await apiClient.get('/roles/plantillas');
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Crear rol desde plantilla
 */
export const createRoleFromTemplate = async (templateId, roleData) => {
  try {
    const response = await apiClient.post(`/roles/plantillas/${templateId}/crear`, {
      name: roleData.name,
      display_name: roleData.display_name,
      description: roleData.description,
      color: roleData.color,
      customize_permissions: roleData.customize_permissions || false,
      additional_permissions: roleData.additional_permissions || [],
      removed_permissions: roleData.removed_permissions || []
    });
    
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export default {
  getRoles,
  getAllRoles,
  getRole,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  getRolePermissions,
  assignPermissions,
  getRoleUsers,
  duplicateRole,
  validateUniqueRoleName,
  getRoleStats,
  exportRoles,
  importRoles,
  getRoleHierarchy,
  getRoleTemplates,
  createRoleFromTemplate
};
